import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import SEOHead from './SEOHead';
import { 
  ArrowRight,
  Scale,
  Stethoscope,
  Building2,
  Smile,
  Factory,
  ShoppingBag,
  Home,
  Heart,
  GraduationCap,
  Landmark,
  Phone
} from 'lucide-react';

const industries = [
  {
    name: 'Law Firms',
    slug: 'law-firm-it-support-toronto',
    icon: Scale,
    description: 'Secure IT solutions for legal practices. Client confidentiality, encrypted communications, e-discovery support, and after-hours filing deadline assistance.',
    highlights: ['Client Confidentiality', 'E-Discovery Ready', 'LSUC Compliance']
  },
  {
    name: 'Medical & Healthcare',
    slug: 'medical-practice-it-support-toronto',
    icon: Stethoscope,
    description: 'PHIPA-compliant IT for medical practices, dental offices, and clinics. EMR/EHR support, patient data security, and reliable uptime.',
    highlights: ['PHIPA Compliance', 'EMR Support', 'Patient Data Security']
  },
  {
    name: 'Real Estate & Accounting',
    slug: 'real-estate-accounting-it-support-toronto',
    icon: Building2,
    description: 'Mobile-ready IT for real estate agents and accountants. Secure remote access, PIPEDA compliance, and industry software support.',
    highlights: ['PIPEDA Compliance', 'Remote Access', 'Tax Season Support']
  },
  {
    name: 'Dental Offices',
    slug: 'industries/dental-offices',
    icon: Smile,
    description: 'Specialized IT for dental practices. Digital imaging integration, practice management software support, and patient record security.',
    highlights: ['Digital Imaging', 'Practice Management', 'PHIPA Compliance']
  },
  {
    name: 'Manufacturing',
    slug: 'manufacturing-it-support',
    icon: Factory,
    description: 'Industrial IT solutions for manufacturing facilities. Production system integration, ERP support, OT/IT integration, SCADA security, and 24/7 production support.',
    highlights: ['Production Systems', '24/7 Support', 'OT/IT Integration']
  },
  {
    name: 'Retail',
    slug: 'industries/retail',
    icon: ShoppingBag,
    description: 'Retail IT solutions from POS systems to e-commerce. Inventory management, multi-location support, and PCI compliance for payment processing.',
    highlights: ['POS Systems', 'PCI Compliance', 'Multi-Location']
  },
  {
    name: 'Non-Profits',
    slug: 'industries/non-profits',
    icon: Heart,
    description: 'Budget-friendly IT for non-profit organizations. Grant management systems, donor databases, and discounted technology programs.',
    highlights: ['Budget-Friendly', 'Donor Management', 'Grant Compliance']
  },
  {
    name: 'Education',
    slug: 'industries/education',
    icon: GraduationCap,
    description: 'IT solutions for schools and educational institutions. Student information systems, e-learning platforms, and network security for campuses.',
    highlights: ['E-Learning', 'Student Systems', 'Campus Security']
  },
  {
    name: 'Government',
    slug: 'industries/government',
    icon: Landmark,
    description: 'Secure IT for government agencies and public sector. Compliance with government security standards, citizen data protection, and transparency requirements.',
    highlights: ['Security Standards', 'Data Protection', 'Compliance']
  }
];

const IndustriesHubPage = () => {
  return (
    <>
      <SEOHead
        title="Industries We Serve | IT Support for Toronto Businesses | King of IT"
        description="King of IT provides specialized IT support for law firms, medical practices, real estate, accounting, manufacturing, retail, non-profits, education, and government in Toronto and the GTA."
        path="/industries"
      />

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-br from-secondary via-secondary to-secondary/95 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
              Industry-Specific IT Solutions
            </Badge>
            <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              IT Support for <span className="text-gradient-gold">Every Industry</span> in Toronto
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              Every industry has unique technology challenges, compliance requirements, and workflows. 
              King of IT provides specialized IT solutions tailored to your sector — not one-size-fits-all support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:6479513653">
                <Button size="lg" className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 647-951-3653
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  Get a Free Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-4">
              Choose Your Industry
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Click on your industry to learn how we can help with your specific IT challenges and compliance requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {industries.map((industry) => {
              const IconComponent = industry.icon;
              return (
                <Link 
                  key={industry.slug} 
                  to={`/${industry.slug}`}
                  className="group"
                  data-testid={`industry-card-${industry.slug.replace(/\//g, '-')}`}
                >
                  <Card className="h-full p-6 hover:shadow-card-hover transition-all duration-300 border-border hover:border-primary/50 group-hover:-translate-y-1">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-semibold text-lg text-foreground mb-2 group-hover:text-primary transition-colors">
                          {industry.name}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                          {industry.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {industry.highlights.map((highlight) => (
                            <Badge 
                              key={highlight} 
                              variant="secondary" 
                              className="text-xs bg-muted text-muted-foreground"
                            >
                              {highlight}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center text-primary font-medium text-sm group-hover:underline">
                          Learn More
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-white mb-4">
            Don't See Your Industry?
          </h2>
          <p className="text-gray-300 mb-8">
            We support businesses across all sectors. Contact us to discuss your specific IT needs 
            and how we can tailor our services to your industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                Contact Us Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:6479513653">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <Phone className="w-5 h-5 mr-2" />
                647-951-3653
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default IndustriesHubPage;
